/**
 * **The sloop's wake** — a bow V and a stern foam trail, built as flat
 * `BARON.PAL` polygons lying on her own waterline.
 *
 * The arithmetic, with no three.js in it, so every number below is checkable
 * without a GPU (`test/ship-wake.test.ts`). `three/ship-wake-ribbons.ts` is the
 * half that turns these answers into a mesh, and `flight/scene-ships.ts` is what
 * feeds it a ship.
 *
 * ## It is a picture, and it is app-side for the reason the whole feature is
 *
 * The wake reads the ship's **pose output** and nothing else: it is a function
 * of where she was, which `ship-patrol.ts` will answer for any past instant
 * because a patrol is closed-form in the clock. So there is no history buffer,
 * no emitter, no warm-up, and nothing to serialize — a wake drawn on the first
 * frame after a load is the wake she has been leaving all along, and a killcam
 * frame drawn at `dt = 0` draws exactly the same one.
 *
 * That matters beyond tidiness. ENHANCED SIMULATION's whole promise is that the
 * engine is **bit-zero with the switch off** (`ship-sim.md` §1), which forbids
 * putting anything new into hashed state. A trail of emitted puffs would have
 * been state; a closed-form curve read off a pose is not, and this file lives in
 * `apps/web` where it cannot reach the engine's dice even by accident.
 *
 * ## The angle is not ours
 *
 * The divergent waves either side of a ship's bow lie inside a wedge whose
 * half-angle is **`arcsin(1/3)` = 19.47°**, a result Lord Kelvin proved in 1887
 * and the one genuinely citable number in this file. It is **independent of
 * speed**, which is why the V here opens by the same amount however fast she is
 * going and only its *length* scales.
 *
 * The caveat is real and does not bite: the constant angle holds at displacement
 * Froude numbers below about 0.5, and above that the apparent wedge narrows like
 * `1/U` (Rabaud & Moisy 2013, and the papers arguing about it since). This sloop
 * runs at `Fr = U/√(gL)` = `8.746 / √(9.81 × 81.61)` = **0.31**, comfortably
 * inside Kelvin's regime — `ship-wake.test.ts` measures that rather than
 * asserting it in a comment.
 *
 * ## Why polygons and not sprites
 *
 * `sprite-effects.ts` draws every screen-space effect this port has, and a wake
 * is the one thing it could not draw: its sheets are the game's own `SMOKE.BMP`
 * and `EXPLO.BMP`, which are a dark grey ramp and a fire ramp, and there is no
 * foam art anywhere in Red Baron's data. Inventing a sprite sheet would have
 * been a bigger invention than this is.
 *
 * A flat coloured polygon lying on the water is what the rest of the picture is
 * made of: the sea is one, the ground is a few thousand, and the ship herself is
 * 3 076. So the wake is three ribbons of triangles with a `BARON.PAL` entry at
 * every vertex — the same discipline `fire-palette.ts` states for particles, and
 * `ship-design.md` §6 for the hull.
 *
 * **Opaque, with no alpha at all.** The fade is a walk *down a palette ramp
 * toward the sea's own colour*, not a fade of opacity — which is both what a
 * 256-colour game could do and a way past this renderer's standing trap, that a
 * mesh which opts out of the depth buffer under reversed-Z draws zero pixels
 * (`combat-visuals.ts`'s note on `bulletTracerMaterial`).
 *
 * The one thing the ramp cannot hide: **`BARON.PAL` has no mid-tone blue.** Its
 * pale family (48–63, the haze the horizon is drawn in) bottoms out at
 * `#71aac3` and its water family (1–7) tops out at `#416582`, and there is
 * nothing between. So the fade takes one visible step near the tail. That is the
 * medium rather than a defect — it is the same step the original's own sky meets
 * its own sea with — and the tail is tapered so the step falls where the ribbon
 * is already narrowing.
 */

import { SHIP_BEAM_M, SHIP_LENGTH_OVERALL_M, SHIP_SPEED_MPS } from "@red-opus/engine";
import { WATER_PALETTE_INDEX } from "../map/map-features.ts";

/** Lord Kelvin, 1887: the half-angle of the wedge a ship's divergent waves live in, in deep water. CITED. */
export const KELVIN_HALF_ANGLE_RAD = Math.asin(1 / 3);

/** How long the wake is, in ship lengths, at her service speed. OURS — long enough to read, short enough to end on screen. */
export const WAKE_LENGTH_LOA = 6;

/**
 * Seconds the foam stays on the water. OURS.
 *
 * Stated as a *time* rather than a length because that is what makes the wake
 * scale off speed honestly: foam lives for so long and the ship runs away from
 * it, so a slower ship leaves a shorter wake with no second constant. At
 * `SHIP_SPEED_MPS` it works out at `WAKE_LENGTH_LOA` ship lengths — 490 m,
 * fifty-six seconds — which is the number that was actually chosen by eye.
 */
export const WAKE_LIFE_S = (WAKE_LENGTH_LOA * SHIP_LENGTH_OVERALL_M) / SHIP_SPEED_MPS;

/** Stations along each ribbon, minus one. 24 puts them 20 m apart at service speed. */
export const WAKE_SEGMENTS = 24;

/** Stations per ribbon. */
export const WAKE_STATIONS = WAKE_SEGMENTS + 1;

/** Lanes across a ribbon: one edge, the crest, the other edge. */
export const WAKE_LANES = 3;

/** Stern trail, port bow arm, starboard bow arm. */
export const WAKE_RIBBONS = 3;

/** Index of the stern trail among the three. */
export const WAKE_TRAIL_RIBBON = 0;

export const WAKE_VERTEX_COUNT = WAKE_RIBBONS * WAKE_STATIONS * WAKE_LANES;
/** Two quads per segment per ribbon, six indices each. */
export const WAKE_INDEX_COUNT = WAKE_RIBBONS * WAKE_SEGMENTS * (WAKE_LANES - 1) * 6;

/** Half-width of the churn right astern of her, metres — her own half-beam, which is the water her stern actually stirs. */
const TRAIL_HALF_BEAM_M = SHIP_BEAM_M / 2;

/** How fast the stern trail spreads, radians of half-angle. OURS, and far narrower than the Kelvin wedge because the turbulent wake is. */
const TRAIL_SPREAD_RAD = (3 * Math.PI) / 180;

/** Half-width of a divergent crest where it leaves the bow, metres. OURS — a quarter-beam line of broken water. */
const CREST_HALF_WIDTH_M = SHIP_BEAM_M / 8;

/** How fast a crest thickens, radians of half-angle. OURS. */
const CREST_SPREAD_RAD = (1.2 * Math.PI) / 180;

/**
 * The last fifth of every ribbon narrows to nothing.
 *
 * A real wake does not end; it thins below noticing. A finite ribbon has to stop
 * somewhere, and stopping at full width draws a bar of foam across the sea at a
 * fixed distance astern, which reads as a bug. Tapering is the honest way to end
 * it, and it also puts the palette's one unavoidable colour step (see the header)
 * where the ribbon is already going away.
 */
const TAPER_FRACTION = 0.2;

/** Below this she leaves nothing at all — a twentieth of her service speed, about nine-tenths of a knot. */
export const WAKE_MIN_SPEED_MPS = SHIP_SPEED_MPS / 20;

/**
 * Metres the first ribbon is lifted off the water, and metres between one
 * ribbon and the next. Fifteen centimetres, and it is a depth-buffer
 * separation rather than a picture.
 *
 * It has to clear **two** ties. The foam lies on the water and the water is a
 * plate, so a ribbon at the waterline exactly is coplanar with the sea; and on
 * a turn the inner bow arm swings across the stern trail, so the ribbons are
 * coplanar with each other. Both showed as a stipple on the first capture of
 * the orbit clip.
 *
 * **Two centimetres was the first attempt and it was not enough**, and the
 * arithmetic says why rather than the picture: on this renderer's reversed-Z
 * float depth with a 1 m near plane, the resolvable step at range `d` is about
 * `d² × 6e-8` — 2.4 mm at 200 m, but **2.9 cm at 700 m**, and a wake is 490 m
 * long seen from a camera hundreds of metres off. Fifteen holds out to a
 * kilometre and a half. Seen from the deck it is a twentieth of the ship's
 * freeboard; seen from anywhere else it is under a tenth of a degree.
 */
export const WAKE_RIBBON_LIFT_M = 0.15;

/**
 * The foam ramp: `BARON.PAL` entries from the crest to the sea, whitest first.
 *
 * Fourteen rungs, every one a real entry of the shipped palette, and the last is
 * **`WATER_PALETTE_INDEX` itself** — the index this port measured the original's
 * own water rendering at (`map/map-features.ts` §7.2) — so the wake does not
 * fade to a colour of ours, it fades into the sea.
 *
 * The middle is **48–63 walked two at a time**, which is not an arbitrary run:
 * `sky-run.ts` recovered it as the original's own *daytime sky* run,
 * `setSkyShades(48, 63, …)`. Borrowing the sky's ramp for foam is a port choice
 * and is stated as one — it is the only pale family a 256-entry palette with no
 * white-blue anywhere has, and thin broken water takes its colour off the sky in
 * any case. 87 `#d7d7d7` above it is the palette's whitest neutral short of the
 * fire ramp, and 195, 7 and 5 are the rungs down into the water family.
 *
 * **49 is skipped, and it is the palette that skipped it**: 48 `#7db6c3` is
 * *brighter* than 49 `#7db6be` (166.4 against 165.9 in luminance, because the
 * blue channel goes up where the others hold), so a ramp carrying both would
 * climb a rung on its way down. The step-by-two through the sky run is a walk
 * down that run's own luminance and not a walk down its indices, and
 * `ship-wake.test.ts` measures the descent rather than trusting this sentence.
 */
export const FOAM_RAMP_INDICES: readonly number[] = [
  87, 63, 61, 59, 57, 55, 53, 51, 48, 195, 7, 5, WATER_PALETTE_INDEX,
];

/**
 * Those indices' colours, in the same order.
 * `test/ship-wake.test.ts` re-derives them from
 * `data/baked/palettes/BARON.PAL.json` and fails if one is not the real byte, so
 * this list cannot drift from the file — the idiom `sky-run.ts` and
 * `world/terrain-palette.ts` already use.
 */
export const FOAM_RAMP_COLOURS: readonly (readonly [number, number, number])[] = [
  [215, 215, 215],
  [182, 182, 182],
  [174, 182, 182],
  [166, 182, 182],
  [158, 182, 186],
  [150, 182, 186],
  [142, 182, 190],
  [134, 182, 190],
  [125, 182, 195],
  [113, 170, 195],
  [65, 101, 130],
  [52, 81, 109],
  [48, 73, 101],
];

/** Rungs an edge vertex sits below its own crest, so a ribbon has soft sides without any alpha. */
const EDGE_RUNG_STEP = 3;

/** Where a ship was, and which way she was pointing, at some past instant. */
export interface WakePose {
  readonly x: number;
  readonly z: number;
  readonly headingRad: number;
}

/** Her pose `ageS` seconds ago. `scene-ships.ts` builds one out of `patrolPoseAt`. */
export type WakePoseAt = (ageS: number) => WakePose;

/** A palette entry as three 0..1 floats. */
export type FoamRamp = Float32Array;

/** Turns a list of 0..255 triples into the 0..1 rungs the mesh writes. */
export function foamRampOf(colours: readonly (readonly number[])[]): FoamRamp {
  const ramp = new Float32Array(colours.length * 3);
  colours.forEach((rgb, rung) => {
    ramp[rung * 3] = (rgb[0] ?? 0) / 255;
    ramp[rung * 3 + 1] = (rgb[1] ?? 0) / 255;
    ramp[rung * 3 + 2] = (rgb[2] ?? 0) / 255;
  });
  return ramp;
}

/** The ramp the mesh is coloured from. */
export const FOAM_RAMP: FoamRamp = foamRampOf(FOAM_RAMP_COLOURS);

/** Vertex slot for one lane of one station of one ribbon. */
export function wakeVertexIndex(ribbon: number, station: number, lane: number): number {
  return (ribbon * WAKE_STATIONS + station) * WAKE_LANES + lane;
}

/**
 * The triangle list, which never changes — two quads across each segment of
 * each ribbon, wound the same way throughout. The mesh is drawn double-sided,
 * so the winding is a convention rather than a claim about which side is up.
 */
export function wakeIndices(): Uint16Array {
  const indices = new Uint16Array(WAKE_INDEX_COUNT);
  let at = 0;
  for (let ribbon = 0; ribbon < WAKE_RIBBONS; ribbon += 1) {
    for (let station = 0; station < WAKE_SEGMENTS; station += 1) {
      for (let lane = 0; lane < WAKE_LANES - 1; lane += 1) {
        const a = wakeVertexIndex(ribbon, station, lane);
        const b = wakeVertexIndex(ribbon, station, lane + 1);
        const c = wakeVertexIndex(ribbon, station + 1, lane + 1);
        const d = wakeVertexIndex(ribbon, station + 1, lane);
        indices.set([a, b, c, a, c, d], at);
        at += 6;
      }
    }
  }
  return indices;
}

/** How far down the ramp the crest has walked at `t` along the ribbon — linear, over the whole ramp. */
function crestRung(t: number): number {
  return Math.min(FOAM_RAMP_INDICES.length - 1, Math.round(t * (FOAM_RAMP_INDICES.length - 1)));
}

/**
 * The colour of every vertex, once.
 *
 * They depend only on which station and which lane a vertex is — not on where
 * the ship is, how fast she is going, or which frame this is — so the mesh's
 * colour attribute is written at construction and never touched again. That is
 * the whole reason the ramp is quantised per vertex rather than per pixel: a
 * fixed ladder of fourteen palette entries can be baked, and a continuous fade
 * could not.
 */
export function writeWakeColours(out: Float32Array, ramp: FoamRamp): void {
  for (let ribbon = 0; ribbon < WAKE_RIBBONS; ribbon += 1) {
    for (let station = 0; station < WAKE_STATIONS; station += 1) {
      const t = station / WAKE_SEGMENTS;
      // The bow crests start one rung down from the stern churn: the whitest
      // water a steamer makes is the broken water off her screw, not the two
      // lines her bow throws.
      const crest = Math.min(
        FOAM_RAMP_INDICES.length - 1,
        crestRung(t) + (ribbon === WAKE_TRAIL_RIBBON ? 0 : 1),
      );
      for (let lane = 0; lane < WAKE_LANES; lane += 1) {
        const rung =
          lane === 1 ? crest : Math.min(FOAM_RAMP_INDICES.length - 1, crest + EDGE_RUNG_STEP);
        const at = wakeVertexIndex(ribbon, station, lane) * 3;
        out[at] = ramp[rung * 3] ?? 0;
        out[at + 1] = ramp[rung * 3 + 1] ?? 0;
        out[at + 2] = ramp[rung * 3 + 2] ?? 0;
      }
    }
  }
}

/** How wide a ribbon is at `t`, before the taper — the stern churn and the bow crests spread at different rates. */
function ribbonHalfWidthM(ribbon: number, t: number, lengthM: number): number {
  const along = t * lengthM;
  const raw =
    ribbon === WAKE_TRAIL_RIBBON
      ? TRAIL_HALF_BEAM_M + along * Math.tan(TRAIL_SPREAD_RAD)
      : CREST_HALF_WIDTH_M + along * Math.tan(CREST_SPREAD_RAD);
  if (t <= 1 - TAPER_FRACTION) return raw;
  return raw * ((1 - t) / TAPER_FRACTION);
}

/**
 * Writes the wake's vertex positions for a ship at `speedMps` whose past poses
 * `poseAt` will answer for, and says whether there is a wake at all.
 *
 * `false` means **nothing was written and nothing should be drawn**: she is
 * stopped, or as near as makes no difference. The caller hides the mesh rather
 * than drawing a degenerate one.
 *
 * Everything scales off the speed and only the speed:
 *
 * - the wake's **length** is `speed × WAKE_LIFE_S`, so she outruns her own foam
 *   by exactly as much as she is going;
 * - its **width** scales with her fraction of service speed, so a ship barely
 *   under way leaves a thread and a ship at full revolutions leaves a road;
 * - its **angle** does not scale, because Kelvin's does not.
 */
export function writeWakePositions(
  out: Float32Array,
  baseVertex: number,
  poseAt: WakePoseAt,
  speedMps: number,
  waterY: number,
): boolean {
  if (!(speedMps > WAKE_MIN_SPEED_MPS)) return false;
  const lengthM = speedMps * WAKE_LIFE_S;
  const vigour = Math.min(1, speedMps / SHIP_SPEED_MPS);
  const halfHull = SHIP_LENGTH_OVERALL_M / 2;
  const spread = Math.tan(KELVIN_HALF_ANGLE_RAD);

  for (let station = 0; station < WAKE_STATIONS; station += 1) {
    const t = station / WAKE_SEGMENTS;
    const pose = poseAt(t * WAKE_LIFE_S);
    // The body frame at that instant: bow toward −z of her head
    // (`math3d.ts`'s `BODY_FORWARD`), starboard a quarter-turn round from it.
    const forwardX = Math.sin(pose.headingRad);
    const forwardZ = -Math.cos(pose.headingRad);
    const starboardX = -forwardZ;
    const starboardZ = forwardX;
    const along = t * lengthM;

    for (let ribbon = 0; ribbon < WAKE_RIBBONS; ribbon += 1) {
      // The stern trail is laid from where her stern was; the two bow crests
      // from where her bow was, thrown abeam by the Kelvin spread — abeam of
      // the head she was *holding at the time*, which is what makes the V bend
      // round a turn with her instead of pivoting about her present heading.
      const trail = ribbon === WAKE_TRAIL_RIBBON;
      const hull = trail ? -halfHull : halfHull;
      const side = trail ? 0 : ribbon === 1 ? -1 : 1;
      const abeam = side * along * spread;
      const centreX = pose.x + forwardX * hull + starboardX * abeam;
      const centreZ = pose.z + forwardZ * hull + starboardZ * abeam;
      const half = vigour * ribbonHalfWidthM(ribbon, t, lengthM);
      const y = waterY + (ribbon + 1) * WAKE_RIBBON_LIFT_M;
      for (let lane = 0; lane < WAKE_LANES; lane += 1) {
        // The width is laid off abeam of her head rather than perpendicular to
        // the crest's own 19.5° run, which makes a crest 6 % wider than it is
        // thick. At a metre and a quarter that is eight centimetres.
        const offset = (lane - 1) * half;
        const at = (baseVertex + wakeVertexIndex(ribbon, station, lane)) * 3;
        out[at] = centreX + starboardX * offset;
        out[at + 1] = y;
        out[at + 2] = centreZ + starboardZ * offset;
      }
    }
  }
  return true;
}
